import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { PermissionRequirements, SideNavItem } from 'app/models/SideNavItem';
import { User } from 'app/models/User';
import {
  Breakpoint,
  MediaQueryService
} from 'app/services/media-query.service';
import { Observable } from 'rxjs';
import { SideNavItemDirective } from './side-navigation-item.directive';

@Component({
  selector: 'app-side-navigation',
  templateUrl: './side-navigation.component.html',
  styleUrls: [
    './side-navigation.component.scss',
    './side-navigation-responsive.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SideNavigationComponent implements OnInit, OnChanges {
  @Input() navTitle: string;
  @Input() availableSections: SideNavItem[];
  @Input() enabledSectionIds: string[];
  @Input() activeSection: string;
  @Input() hideChangeLog: boolean;
  @Input() type: string;

  @Input() isFollowing: boolean;

  @Output() navItemClick: EventEmitter<SideNavItem> = new EventEmitter();
  @Output() navCloseClick: EventEmitter<Event> = new EventEmitter();
  @Output() showChangelogClick: EventEmitter<Event> = new EventEmitter();

  @ViewChildren(SideNavItemDirective) navItems: QueryList<SideNavItemDirective>;
  @ViewChild('navScroll') navScroll: ElementRef<HTMLElement>;

  @HostBinding('class.open') @Input() showMobile: boolean;

  tablet$: Observable<boolean>;

  constructor(
    private user: User,
    private mediaQuery: MediaQueryService,
    private element: ElementRef
  ) {
    this.tablet$ = this.mediaQuery.lt(Breakpoint.lg);
    this._onSidebarClick = this._onSidebarClick.bind(this);
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeSection) {
      this.scrollToItem(changes.activeSection.currentValue);
    }

    if (changes.showMobile) {
      if (changes.showMobile.currentValue) {
        this.element.nativeElement.addEventListener(
          'click',
          this._onSidebarClick
        );
      } else {
        this.element.nativeElement.removeEventListener(
          'click',
          this._onSidebarClick
        );
      }
    }
  }

  trackByKey(index: number, item: SideNavItem) {
    return item.key;
  }

  navLinkClick(evt: Event, item: SideNavItem): void {
    evt.preventDefault();

    this.scrollToItem(item.key);
    this.navItemClick.emit(item);
    this.navCloseClick.emit(evt);
  }

  scrollToItem(key: string) {
    if (this.navItems) {
      let affected = this.navItems.find(item => item.key === key);

      if (affected) {
        let scrollEl = this.navScroll.nativeElement;

        if (affected.index === 0) {
          scrollEl.scrollTo({
            top: 0,
            behavior: 'smooth'
          });

          return;
        } else if (affected.index === this.navItems.length - 1) {
          scrollEl.scrollTo({
            top: scrollEl.scrollHeight,
            behavior: 'smooth'
          });

          return;
        }

        let scrollBounds = this.navScroll.nativeElement.getBoundingClientRect();

        let affectedEl = affected.element.nativeElement;

        let elBounds = affectedEl.getBoundingClientRect();

        const comfortPadding = 12;
        const magicBottomPadding = 16;

        const bottomOffset = magicBottomPadding + comfortPadding;

        if (elBounds.bottom > scrollBounds.bottom - bottomOffset) {
          scrollEl.scrollBy({
            top: elBounds.bottom - scrollBounds.bottom + bottomOffset,
            behavior: 'smooth'
          });
        } else if (elBounds.top < scrollBounds.top + comfortPadding) {
          scrollEl.scrollBy({
            top: elBounds.top - scrollBounds.top - comfortPadding,
            behavior: 'smooth'
          });
        }
      }
    }
  }

  hasPermissions(permissions: PermissionRequirements) {
    if (!permissions) return true;

    if (permissions.mustHave) {
      for (let p of permissions.mustHave) {
        if (p === 'follower') {
          if (!this.isFollowing) return false;
        } else if (p === 'enterprise') {
          if (this.user.isFreemium()) return false;
        } else if (!this.user.session.features.includes(p)) {
          return false;
        }
      }
    }

    if (permissions.mustNotHave) {
      for (let p of permissions.mustNotHave) {
        if (p === 'follower') {
          if (this.isFollowing) return false;
        } else if (p === 'enterprise') {
          if (!this.user.isFreemium()) return false;
        } else if (this.user.session.features.includes(p)) {
          return false;
        }
      }
    }

    if (permissions.oneOf) {
      for (let p of permissions.oneOf) {
        if (p === 'follower') {
          if (this.isFollowing) return true;
        } else if (p === 'enterprise') {
          if (!this.user.isFreemium()) return true;
        } else if (this.user.session.features.includes(p)) {
          return true;
        }
      }

      return false;
    }

    return true;
  }

  _onSidebarClick(evt) {
    let el = this.element.nativeElement;

    let rect = el.getBoundingClientRect();

    if (evt.clientX > rect.left && evt.clientX < rect.right) {
      return;
    }

    this.navCloseClick.emit(evt);

    evt.preventDefault();
  }
}
